<div class="flex flex-1 flex-col gap-2 h-full w-full p-3">
    @if(hasInnDocFeature() && !isLoadingCounts) {
    <!-- Top Bar -->
    <div class="flex items-center gap-3">
        <app-menu #optionsMenu [items]="optionsMenuItems" (itemSelected)="onOptionSelect($event)"
            (visibleChange)="isOptionsMenuOpen = $event" [pTooltip]="'Options'" tooltipPosition="top">
            <app-button variant="filled" severity="primary" icon="pi pi-plus" iconPosition="left"
                (clicked)="openOptionsPanel($event)" [label]="'Create Document'" />
        </app-menu>
        <div class="w-[25%]">
            <app-search-bar placeholder="Search Document" [(ngModel)]="searchValue" (searchChange)="onSearch()"
                width="100%"></app-search-bar>
        </div>

        <div class="flex items-center gap-2 ml-auto text-sm">
            <!-- View Toggle -->
            <app-select-button [options]="viewModeOptions" [(ngModel)]="viewMode"
                (selectionChange)="onViewModeChange($event)" size="small">
            </app-select-button>

            <!-- Sort -->
            <app-sort-popover [fields]="sortFields" [(sortField)]="sortField" [(sortOrder)]="sortOrder"
                (sortChange)="onSortChange()" />

            <!-- Filter -->
            <app-button variant="outlined" severity="contrast" icon="pi pi-filter" iconPosition="left"
                (clicked)="toggleFilterPanel()" [label]="'Filters'" />
        </div>
    </div>

    <!-- Documents Grid/List View -->
    <div class="flex-1 min-h-0 flex flex-col">
        <ng-container *ngIf="!tableConfig.loading && contracts.length === 0; else dataBlock">
            <div class="w-full flex justify-center h-full flex-1 items-center">
                <div class="flex flex-col w-[25%] text-center">
                    <img class="z-10 align-middle mx-auto" [src]="'images/no_documents.png' | assetUrl" alt=""
                        height="200" width="200" draggable="false">
                    <span class="text-xl font-bold mt-4">{{"No documents found" | titlecase}}</span>
                    <span class="text-gray-600">There are currently no documents</span>
                </div>
            </div>
        </ng-container>

        <ng-template #dataBlock>
            <app-grid-list-view [items]="contracts" [viewMode]="viewMode" [gridItemTemplate]="gridItemTemplate"
                [actionBarTemplate]="actionBarTemplate" [(selectedItems)]="selectedContracts" [dataKey]="'_id'"
                [scrollable]="true" [rows]="rows" [rowsPerPageOptions]="[20, 30, 40]"
                [useDataTable]="viewMode === 'list'" [tableColumns]="tableColumns" [tableConfig]="tableConfig"
                [customTemplates]="templatesMap" [totalRecords]="totalRecords" [first]="first" [showPaginator]="true"
                (pageChange)="onPageChange($event)" [maxHeight]="'calc(100vh - 12rem)'"
                [parentClass]="'flex-1 flex flex-col min-h-0 h-full'" [itemsLoading]="tableConfig.loading || false"
                [gridCols]="1" [gridColsSm]="1" [gridColsMd]="2" [gridColsLg]="2" [gridColsXl]="4"
                [gridCols2xl]="4"></app-grid-list-view>

            <!-- Grid Item Template -->
            <ng-template #gridItemTemplate let-document>
                <app-document-card [document]="document" [currentUser]="currentUser" [showProjectName]="true"
                    [contractMenuItems]="getMenuItems(document)" (documentClick)="openContract($event)"
                    (menuClick)="handleMenuClick($any($event).event, $any($event).documentId, $any($event).documentName, $any($event).item)"
                    (menuToggle)="handleClick($any($event).event, $any($event).menu)"
                    (popoverToggle)="handlePopoverToggle($event)">
                </app-document-card>
            </ng-template>

            <!-- Action Bar Template -->
            <ng-template #actionBarTemplate>
                @if(selectedContracts && selectedContracts.length > 0) {
                <div class="action-bar">
                    <div class="action-bar-content">
                        <p class="text-sm font-medium">
                            {{ selectedContracts.length }} {{ selectedContracts.length === 1 ? 'item' : 'items' }} is
                            selected
                        </p>
                        <div class="action-bar-divider"></div>
                        <app-button variant="outlined" label="{{'Add to workspace knowledge' | titlecase}}"
                            severity="contrast" [icon]="KNOWLEDGE_BASE_SVG.KNOWLEDGE_BASE_ICON" [iconSource]="'svg'"
                            [iconPosition]="'left'" (clicked)="openAddToWorkspaceKnowledgeDialog()"
                            [enableThrottle]="true" />
                        <app-button variant="outlined" label="{{'Add to project knowledge' | titlecase}}"
                            severity="contrast" [icon]="KNOWLEDGE_BASE_SVG.OPEN_BOOK_CHECK" [iconSource]="'svg'"
                            [iconPosition]="'left'" (clicked)="openAddToProjectKnowledgeDialog()"
                            [enableThrottle]="true" />
                    </div>
                </div>
                }
            </ng-template>
        </ng-template>
    </div>

    <!-- Data Table Templates -->
    <!-- Document Name Template -->
    <ng-template #documentNameTemplateRef let-document="$implicit">
        <div class="flex items-center gap-2 cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-1 rounded transition-all max-w-[10rem]"
            (click)="openContract(document._id)">
            <div class="flex-1">
                <div class="font-medium text-gray-900 hover:text-arena-orange transition-colors truncate max-w-[14rem]"
                    [pTooltip]="document?.name ?? 'Untitled Document'" tooltipPosition="bottom" [showDelay]="300">
                    {{document?.name ?? 'Untitled Document'}}
                </div>
            </div>
        </div>
    </ng-template>

    <!-- Project Template -->
    <ng-template #projectTemplateRef let-document="$implicit">
        <div class="text-sm truncate" [ngClass]="document.project?.name ? 'text-gray-700' : 'text-gray-400 italic'"
            [pTooltip]="document.project?.name || 'No project assigned'" tooltipPosition="bottom" [showDelay]="300">
            {{ document.project?.name || 'No project assigned' }}
        </div>
    </ng-template>

    <!-- Document Type Template -->
    <ng-template #documentTypeTemplateRef let-document="$implicit">
        <div class="text-sm text-gray-700 truncate max-w-[8rem]"
            [pTooltip]="getDocumentTypeName(document.linkedDocType) || 'No type'" tooltipPosition="bottom"
            [showDelay]="300">
            {{ getDocumentTypeName(document.linkedDocType) || ('No type' | titlecase) }}
        </div>
    </ng-template>

    <!-- Assignees Template -->
    <ng-template #assigneesTemplateRef let-document="$implicit">
        <div class="truncate text-sm text-gray-600 flex items-center gap-2 justify-center">
            <app-avatar-stack [users]="document.assignees" [visibleAvatars]="2" size="xs">
            </app-avatar-stack>
        </div>
    </ng-template>

    <!-- Status Template -->
    <ng-template #statusTemplateRef let-document="$implicit">
        <div class="rounded-base px-2 py-1 inline-flex items-center justify-center theme-text-border w-fit"
            [pTooltip]="getDocumentStatusName(document)" tooltipPosition="bottom" [showDelay]="300">
            <span class="theme-text text-xs font-medium whitespace-nowrap">{{ getDocumentStatusName(document) }}</span>
        </div>
    </ng-template>

    <!-- Due Date Template -->
    <ng-template #dueDateTemplateRef let-document="$implicit">
        <div class="text-sm"
            [ngClass]="{'text-danger font-semibold': document.dueDateExceeding, 'font-light': !document.dueDateExceeding}"
            [pTooltip]="(document.dueDate | date: 'dd MMM yyyy') ?? 'No due date'" tooltipPosition="bottom"
            [showDelay]="300">
            {{document.dueDate | date: 'dd MMM yyyy' }}
        </div>
    </ng-template>

    <!-- Upcoming Milestone Template -->
    <ng-template #upcomingMilestoneTemplateRef let-document="$implicit">
        <div class="truncate text-sm text-gray-600">
            @if(document.upcomingMilestone && document.upcomingMilestone.length > 0){
            <div class="flex items-center gap-2 text-sm text-gray-600">
                <i class="pi pi-flag text-arena-orange-500"></i>
                <span class="truncate max-w-[10rem]" [pTooltip]="document.upcomingMilestone[0].name"
                    tooltipPosition="bottom" [showDelay]="300">{{
                    document.upcomingMilestone[0].name }}</span>
                <span class="text-gray-400">•</span>
                <span class="rounded-base bg-arena-orange-100 text-arena-orange py-1 px-2 w-fit whitespace-nowrap">
                    {{ document.upcomingMilestone[0].date | date: 'MMM d' }}
                </span>
            </div>
            }@else {
            <div class="text-sm text-gray-400 italic flex items-center gap-2">
                <i class="pi pi-flag"></i>
                <span>{{'No upcoming milestones'}}</span>
            </div>
            }
        </div>
    </ng-template>

    <!-- Date Updated Template -->
    <ng-template #dateUpdatedTemplateRef let-document="$implicit">
        <div class="truncate text-sm text-gray-600"
            [pTooltip]="(document.updatedAt | dateFormatter: 'Do MMMM, YYYY') ?? ''" tooltipPosition="bottom"
            [showDelay]="300">
            {{document.updatedAt | dateFormatter: 'Do MMMM, YYYY'}}
        </div>
    </ng-template>

    <!-- Actions Template -->
    <ng-template #actionsTemplateRef let-document="$implicit">
        <div class="flex items-center justify-center" (click)="$event.stopPropagation()">
            <app-popover #menu width="12rem">
                <ng-template #trigger>
                    <app-button icon="pi pi-ellipsis-v" iconPosition="left"
                        (clicked)="openDocumentMenu(document._id, document.name)" />
                </ng-template>
                <ng-template #body>
                    <div class="flex flex-col text-sm">
                        @if(getMenuItems(document) && getMenuItems(document)[0].items) {
                        @for(item of getMenuItems(document)[0].items; track item.label) {
                        <div (click)="handleMenuAction(item.label!, menu)"
                            class="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
                            [ngStyle]="item.style">
                            <i [class]="item.icon" class="text-sm"></i>
                            <span>{{ item.label }}</span>
                        </div>
                        }
                        }
                    </div>
                </ng-template>
            </app-popover>
        </div>
    </ng-template>
    }
</div>

<app-dialog [visible]="rename.open" title="Rename Document" [fullscreenOnMobile]="true" class="max-w-[30rem]"
    (closed)="resetRename()">
    <ng-template #bodyTemplate>
        <div class="flex flex-col px-6 py-4 gap-3">
            <p class="mb-0">Enter the new name for the Document:</p>
            <input class="input" pInputText id="newContractName" (click)="($event.stopPropagation())"
                [(ngModel)]="rename.newName" autocomplete="off" />
        </div>
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end items-center gap-2">
            <app-button label="Cancel" (clicked)="rename.open = false" variant="outlined" severity="contrast" />
            <app-button label="Save" icon="pi pi-save" (clicked)="renameContract($event)" [enableThrottle]="true"
                variant="filled" severity="primary" />
        </div>
    </ng-template>
</app-dialog>

<app-dialog [visible]="dueDate.open" title="Due Date" [fullscreenOnMobile]="true" class="max-w-[30rem]"
    (closed)="resetDueDate()">
    <ng-template #bodyTemplate>
        <div class="flex flex-col px-6 py-4 gap-3">
            <p class="mb-0">Select the Due Date for this document:</p>
            <app-datepicker [(value)]="dueDate.selectedDate" [config]="{
                showIcon: true,
                placeholder: 'Select Due Date',
                dateFormat: 'dd M yy',
                appendTo: 'body',
                minDate: currentDate
            }" />
        </div>
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end items-center gap-2">
            <app-button label="Cancel" (clicked)="dueDate.open = false" variant="outlined" severity="contrast" />
            <app-button label="Save" icon="pi pi-save" (clicked)="saveDueDate($event)" [enableThrottle]="true"
                variant="filled" severity="primary" />
        </div>
    </ng-template>
</app-dialog>

@if(deleteDialogVisible){
<app-delete-confirmation [(visible)]="deleteDialogVisible" headerText="Are you sure?"
    bodyText="This action will delete the document permanently" confirmButtonText="Delete" [requireConfirmation]="true"
    (onConfirm)="confirmDeleteContract()">
</app-delete-confirmation>
}

<app-ai-assistant [(visible)]="aiAssistant.open"></app-ai-assistant>

@if(createVisible){
<app-dialog [visible]="createVisible" [fullscreenOnMobile]="true" class="max-w-[50rem] h-[85vh]"
    (closed)="closeCreateDocumentDialog(); onCreateDocumentHide()">
    <ng-template #headerTemplate>
        <div class="text-lg font-bold">Create a document</div>
    </ng-template>
    <ng-template #bodyTemplate>
        <div class="flex flex-col h-full min-h-0">
            <div class="flex-shrink-0">
                <p-tabs [value]="selectedTabIndex().toString()" (valueChange)="handleTabChange($event)"
                    class="custom-tabview w-full">
                    <p-tablist>
                        @for(tab of createDocumentDialogTabs();let i = $index; track tab.id) {
                        <p-tab [value]="i.toString()" [disabled]="tab.disabled" [pTooltip]="tab.tooltip || ''"
                            [tooltipPosition]="'top'" [showDelay]="300" class="flex items-center !gap-2">
                            <div class="flex items-center gap-2">
                                <span class="text-gray-500 font-sans text-sm whitespace-nowrap">{{
                                    tab.label
                                    }}</span>
                            </div>
                        </p-tab>
                        }
                    </p-tablist>
                </p-tabs>
            </div>
            @if(docTypeLoading){
            <p-skeleton width="100%" height="100%" />
            }
            @else{
            @if(selectedTabIndex() === 0){
            <div class="flex-1 flex flex-col min-h-0 overflow-hidden">
                <div class="flex-shrink-0 text-sm my-2 mb-4 font-semibold">Select document type</div>
                <div class="flex-shrink-0 flex flex-row items-center justify-between gap-3 w-full mb-3">
                    <span class="p-input-icon-left w-[40%]">
                        <app-search-bar placeholder="Search with name" [(ngModel)]="docTypeSearch"
                            (searchChange)="onDocTypeFilter()" width="100%"></app-search-bar>
                    </span>
                    <app-button label="New document type" variant="outlined" severity="contrast" icon="pi pi-plus"
                        (clicked)="navigateToAddDocType()" />
                </div>
                <div
                    class="grid md:grid-cols-2 grid-cols-1 gap-2 auto-rows-min flex-1 min-h-0 overflow-y-auto content-start">
                    @for (docType of filteredDocTypes; track docType._id) {
                    <div appThrottleClick
                        class="flex flex-col gap-2 border p-4 justify-between rounded-base items-center cursor-pointer doc-type-item self-start"
                        (throttledClick)="selectDocType(docType)">
                        <div class="flex flex-row gap-2 w-full overflow-hidden">
                            <div class="overflow-hidden">
                                <span class="flex items-center font-semibold text-md">
                                    {{docType?.name }}
                                    @if(docType.containsForm){
                                    <p-chip label="Contains Form"
                                        class="mx-2 bg-arena-orange !px-2 !py-1 rounded-base text-xs"></p-chip>
                                    }
                                </span>
                                <span class="block text-sm text-gray-500 truncate max-w-full"
                                    [pTooltip]="docType.description">{{docType.description}}
                                </span>

                            </div>
                        </div>
                        <div class="flex flex-row gap-2 w-full justify-between">
                            <div class="flex gap-1">
                                <span class="flex items-center text-sm text-gray-500 doc-type-description"
                                    *ngIf="docType.createdAt">Created on</span>
                                <span class="flex items-center text-sm font-semibold " *ngIf="docType.createdAt">
                                    {{ (docType.createdAt) | dateFormatter: 'Do MMM, YYYY' }}
                                </span>
                            </div>
                            <i class="pi pi-chevron-right my-auto"></i>
                        </div>
                    </div>
                    }
                </div>
            </div>
            }
            @else {
            <div class="text-sm my-2 font-semibold">Select document type <span class="text-red-500">*</span></div>
            <div class="flex flex-col gap-3 flex-1 min-h-0">
                <div class="flex flex-col gap-2">
                    <div class="text-xs mt-2 font-semibold">Document type</div>

                    <div class="flex flex-row items-center gap-3 w-full">
                        <app-select [(value)]="selectedDocTypeId" [options]="uploadDocumentTypeOptions"
                            [config]="docTypeSelectConfig" (onChange)="handleDocTypeChange($event)" class="w-full" />

                        <app-button label="New document type" variant="outlined" severity="contrast" icon="pi pi-plus"
                            (clicked)="navigateToAddDocType()" />
                    </div>
                </div>

                <div class="flex-1 min-h-0 overflow-y-auto w-full">
                    <app-file-upload-with-actions class="w-full" [multiple]="true" [fileTypes]="allowedFileTypes"
                        [maxFileSize]="50 * 1000 * 1000" [uploadedFiles]="uploadedFiles" [hideActionButtons]="true"
                        (filesChanged)="uploadedFiles = $event">
                    </app-file-upload-with-actions>
                </div>
            </div>
            }
            }
        </div>
    </ng-template>
    <ng-template #footerTemplate>
        @if(selectedTabIndex() === 1){
        <div class="flex justify-end items-end gap-2">
            <app-button label="Cancel" variant="ghost" severity="contrast" (clicked)="closeCreateDocumentDialog()" />
            <app-button label="Upload" variant="filled" severity="primary" [loading]="loadingState()"
                [disabled]="uploadedFiles.length === 0 || selectedDocTypeId === null" (clicked)="uploadFiles()"
                [enableThrottle]="true" />
        </div>
        }
    </ng-template>
</app-dialog>
}

<app-create-form [docType]="docCreationByDocumentType.doc" [visible]="docCreationByDocumentType.visible"
    (visibleChange)="onDocCreationVisibleChange($event)"></app-create-form>



<app-dialog [visible]="openProjectLinkDialog" title="Link to Project" [fullscreenOnMobile]="true" class="max-w-[30rem]"
    (closed)="openProjectLinkDialog = false">
    <ng-template #bodyTemplate>
        <div class="flex flex-col px-6 py-4 gap-3">
            <p class="mb-0">Select the Project to link this document:</p>
            <app-select [(value)]="selectedProjectId" [options]="projectOptions" [config]="projectSelectConfig"
                (onChange)="handleProjectChange($event)" (onSearch)="handleProjectSearch($event)" />
        </div>
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end items-center gap-2">
            <app-button label="Cancel" (clicked)="openProjectLinkDialog = false" variant="outlined"
                severity="contrast" />
            <app-button label="Save" icon="pi pi-save" (clicked)="linkToProject()" [enableThrottle]="true"
                variant="filled" severity="primary" />
        </div>
    </ng-template>
</app-dialog>

<p-drawer [(visible)]="filterPanelVisible" position="right" class="w-[22rem]">
    <ng-template pTemplate="headless">
        <div class="flex flex-col h-full">
            <div class="flex items-center justify-between border-b p-4">
                <h2 class="text-lg font-semibold">Filter</h2>
                <app-button icon="pi pi-times" (clicked)="toggleFilterPanel()" variant="filled" severity="contrast" />
            </div>
            <div class="flex-1 overflow-y-auto space-y-5 text-sm text-gray-800 p-4">
                <div>
                    <label class="block font-medium mb-1">My Documents</label>
                    <app-select [(value)]="selectedCode" [options]="contractOptions" [config]="contractSelectConfig"
                        (onChange)="handleChange($event)" />
                </div>
                <div>
                    <label class="block font-medium mb-1">Shared with</label>
                    <app-select [(value)]="selectedPeople" [options]="peopleOptions" [config]="peopleSelectConfig"
                        (onChange)="handlePeopleChange($event)" />
                </div>
                <div>
                    <label class="block font-medium mb-1">Document Type</label>
                    <app-select [(value)]="selectedDocTypeId" [options]="docTypeOptions" [config]="docTypeSelectConfig"
                        (onChange)="handleDocTypeChange($event)" />
                </div>
                <div>
                    <label class="block font-medium mb-1">Status Type</label>
                    <app-select [(value)]="selectedStatus" [options]="statusOptions" [config]="statusSelectConfig"
                        (onChange)="handleStatusChange($event)" />
                </div>
                <div>
                    <label class="block font-medium mb-1">Actionable Documents</label>
                    <app-select [(value)]="selectedActionFilter" [options]="actionFilterOptions"
                        [config]="actionFilterSelectConfig" (onChange)="handleActionFilterChange($event)" />
                </div>
                <div>
                    <label class="block font-medium mb-1">Due Date</label>
                    <app-datepicker [(value)]="filterDueDateRange" [config]="{
                        selectionMode: 'range',
                        showIcon: true,
                        appendTo: 'body',
                        dateFormat: 'dd M yy',
                        placeholder: 'Select Due Date'
                    }" class="w-full" />
                </div>
            </div>
            <div class="flex justify-between items-center px-6 py-4 border-t">
                <app-button label="Clear all and apply" (clicked)="clearAllFilters()" [enableThrottle]="true"
                    variant="outlined" severity="contrast" />
                <app-button label="Apply Filter" (clicked)="applyFilters()" [enableThrottle]="true" variant="filled"
                    severity="primary" />
            </div>
        </div>
    </ng-template>
</p-drawer>

<!-- Add to Workspace Knowledge Dialog -->
<app-knowledge-base-dialog [(visible)]="addToWorkspaceKnowledgeDialog.visible" [scope]="'workspace'"
    [workspace]="currentWorkspace?._id"
    [documentCount]="(selectedContracts && selectedContracts.length > 0) ? selectedContracts.length : (currentMenuDocument ? 1 : 0)"
    [uploading]="addToWorkspaceKnowledgeDialog.uploading" (upload)="uploadToWorkspaceKnowledge($event)">
</app-knowledge-base-dialog>

<!-- Add to Project Knowledge Dialog -->
<app-knowledge-base-dialog [(visible)]="addToProjectKnowledgeDialog.visible" [scope]="'project'"
    [documentCount]="(selectedContracts && selectedContracts.length > 0) ? selectedContracts.length : (currentMenuDocument ? 1 : 0)"
    [uploading]="addToProjectKnowledgeDialog.uploading" (upload)="uploadToProjectKnowledge($event)">
</app-knowledge-base-dialog>